import { useEffect, useRef } from 'react'

import { type StringKey, t } from './i18n'
import { BACKGROUND_IMAGES, type BackgroundMode, backgroundThumbPath } from './useBackgroundEffect'

/**
 * Choosing what sits behind you.
 *
 * A sheet rather than a cycle-through-modes button: with six options a toggle
 * would mean tapping five times to get back to your face, and a child mid-lesson
 * should never have to count taps.
 *
 * Rendered from Classroom into `.classroom`, NOT from the control bar. The bar
 * carries a `backdrop-filter`, which makes it the containing block for anything
 * positioned inside it — the trap that put the leave dialog off-screen and would
 * have clipped this sheet. Positioned absolutely so opening it costs the video no
 * layout, and anchored to the bottom centre so it sits directly above the control
 * that opened it on every screen size.
 *
 * Each tile shows a ~2 KB thumbnail. The full 20 KB frame is fetched only when a
 * tile is hovered or focused (see `prefetch`), so opening the sheet costs 12 KB
 * total and choosing feels instant.
 */

interface Props {
  open: boolean
  mode: BackgroundMode
  busy: boolean
  degraded: boolean
  onSelect: (mode: BackgroundMode) => void
  onPrefetch: (mode: BackgroundMode) => void
  onClose: () => void
  returnFocusTo?: React.RefObject<HTMLButtonElement>
}

export function BackgroundPicker({
  open,
  mode,
  busy,
  degraded,
  onSelect,
  onPrefetch,
  onClose,
  returnFocusTo,
}: Props) {
  const firstRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const closeHandler = useRef(onClose)
  closeHandler.current = onClose
  /**
   * Whether closing should pull focus back to the button that opened this.
   *
   * True for keyboard dismissal (Escape), false when the user tapped somewhere
   * else — yanking focus onto the control bar because someone touched the video
   * would be its own small act of rudeness.
   */
  const restoreFocus = useRef(true)

  useEffect(() => {
    if (!open) return
    restoreFocus.current = true
    firstRef.current?.focus()

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeHandler.current()
    }
    /**
     * Tap anywhere else and it goes away.
     *
     * This sheet has no X: it is a popover, and the way out of a popover is to
     * look somewhere else. Without this the only exits were Escape — which a
     * child on a phone does not have — and finding the same small control again,
     * so the panel simply sat there over the lesson.
     */
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (panelRef.current?.contains(target)) return
      // The opening control lives outside this panel (it is in the control bar).
      // Closing here on the way down would let its own click handler reopen the
      // panel on the way up, so a tap on it would look like nothing happened.
      if (returnFocusTo?.current?.contains(target)) return
      restoreFocus.current = false
      closeHandler.current()
    }

    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointerDown)
      if (restoreFocus.current) returnFocusTo?.current?.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ref identity is stable
  }, [open])

  if (!open) return null

  return (
    <div
      className="bgpicker"
      ref={panelRef}
      role="dialog"
      aria-label={t('bg.title')}
      aria-modal="false"
    >
      <div className="bgpicker__head">
        <h2 className="bgpicker__title">{t('bg.title')}</h2>
        {busy ? <span className="bgpicker__busy">{t('bg.applying')}</span> : null}
      </div>

      <div className="bgpicker__grid" role="group" aria-label={t('bg.title')}>
        <Tile
          selected={mode === 'off'}
          label={t('bg.none')}
          onSelect={() => onSelect('off')}
          buttonRef={firstRef}
        >
          <span className="bgpicker__none" aria-hidden="true">
            <PersonIcon />
          </span>
        </Tile>

        <Tile selected={mode === 'blur'} label={t('bg.blur')} onSelect={() => onSelect('blur')}>
          <span className="bgpicker__blur" aria-hidden="true" />
        </Tile>

        {BACKGROUND_IMAGES.map((image) => (
          <Tile
            key={image.id}
            selected={mode === image.id}
            label={t(image.labelKey as StringKey)}
            onSelect={() => onSelect(image.id)}
            // Warm the full frame while the finger is still on its way down.
            onPrefetch={() => onPrefetch(image.id)}
          >
            <img
              className="bgpicker__thumb"
              src={backgroundThumbPath(image.id)}
              alt=""
              width={192}
              height={108}
              loading="lazy"
              decoding="async"
              draggable={false}
            />
          </Tile>
        ))}
      </div>

      {/* Said once, plainly, rather than discovered as jerky video mid-lesson. */}
      {degraded ? <p className="bgpicker__note">{t('bg.slow')}</p> : null}
    </div>
  )
}

function Tile({
  selected,
  label,
  onSelect,
  onPrefetch,
  children,
  buttonRef,
}: {
  selected: boolean
  label: string
  onSelect: () => void
  onPrefetch?: () => void
  children: React.ReactNode
  buttonRef?: React.RefObject<HTMLButtonElement>
}) {
  return (
    <button
      type="button"
      className={`bgpicker__tile ${selected ? 'bgpicker__tile--on' : ''}`}
      onClick={onSelect}
      onPointerEnter={onPrefetch}
      onFocus={onPrefetch}
      // aria-pressed rather than a role=radio group: each tile is independently
      // togglable in the user's mind ("is this one on?"), and radios would demand
      // arrow-key navigation that a touch user never gets.
      aria-pressed={selected}
      ref={buttonRef}
      title={label}
    >
      {children}
      <span className="bgpicker__label">{label}</span>
      {selected ? (
        <span className="bgpicker__check" aria-hidden="true">
          <CheckIcon />
        </span>
      ) : null}
    </button>
  )
}

const PersonIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
    <path d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm0 2c-3.3 0-8 1.7-8 5v1h16v-1c0-3.3-4.7-5-8-5Z" />
  </svg>
)
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d="M9.6 16.8 4.8 12l1.4-1.4 3.4 3.4 8-8L19 7.4Z" />
  </svg>
)
